import { prezzoTesto } from '../utils/prezzo';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useCurrency } from '../hooks/useCurrency';
import { usePricing } from '../hooks/usePricing';
import { useStartCheckout2TB } from '../hooks/useStartCheckout2TB';
import CurrencySelector from '../components/CurrencySelector';
import InAppBrowserWarning from '../components/InAppBrowserWarning';
import { giornoDellaScadenza } from '../utils/dataOfferta';
import { limiteLeggibile } from '../utils/limiti';

// Display text (name/description/features) is looked up from i18n at render
// time via KEY_PREFIX below — this array only carries structural/pricing data.
const PLANS = [
  {
    // Centered, highlighted as "Most popular" — this is the plan we want to
    // push, same as the public pricing section (HomePage.jsx .cps-plans order).
    id: 'lifetime_2tb', name: '2 TB Lifetime', emoji: null, color: '#16a34a',
    storage: '2 TB', description: 'Maximum space, forever', featureCount: 11,
    popular: true,
    priced: true,
  },
  {
    // 5 TB, acceso il 2026-09-07. Sta qui — subito sotto il 2 TB — e non ha
    // nessuna proprieta' di stile propria: usa la stessa struttura degli
    // altri, quindi la card e' identica per costruzione e non "somigliante".
    // Le voci dell'elenco sono le stesse del 2 TB (featuresFrom): sono
    // davvero le stesse, e duplicarle in undici stringhe nuove avrebbe solo
    // creato due testi destinati a divergere.
    id: 'lifetime_5tb', name: '5 TB Lifetime', emoji: null, color: '#15803d',
    storage: '5 TB', description: 'Maximum space, forever', featureCount: 11,
    priced: true, aPosti: true, featuresFrom: 'plan_2tb',
  },
  {
    id: 'lifetime_1tb', name: '1 TB Lifetime', emoji: null, color: '#22c55e',
    storage: '1 TB', description: 'Pay once, keep forever', featureCount: 11,
    founder: true,
    priced: true,
  },
  {
    // IL GRATUITO PER ULTIMO (2026-09-11). Stava in cima: la prima scheda che
    // si vedeva era quella che l'utente ha gia', e i piani a pagamento
    // arrivavano dopo. Sposta solo la posizione — struttura, voci e stringhe
    // sono le stesse di prima, ed e' l'unica scheda senza prezzo perche' non
    // ha `priced` (vedi price-free piu' sotto).
    id: 'free', name: 'Free', emoji: '🆓', color: '#22c55e',
    storage: '1 GB', description: 'Free trial', featureCount: 8,
  },
];
const KEY_PREFIX = { free: 'plan_free', lifetime_1tb: 'plan_1tb', lifetime_2tb: 'plan_2tb', lifetime_5tb: 'plan_5tb' };

export default function PlansPage({ onBack, onRegister }) {
  const { t, i18n } = useTranslation('plans');
  const { user, isAuthenticated } = useAuth();
  const { currency, setCurrency } = useCurrency();
  const { priceOf, founderPricingActive: isLaunch, daysLeft, deadline, offerta1tbAttiva, offerta1tbFine, limiti, posti5tb, posti, prezzo2tbDopo } = usePricing(currency);
  // La data non e' scritta qui: arriva dal server (OFFERTA_1TB_FINE, la
  // stessa costante che decide se il prezzo e' 49 o 59) e si formatta da
  // sola nella lingua corrente. Vedi utils/dataOfferta.js.
  const scadenza1tb = giornoDellaScadenza(offerta1tbFine, i18n.language);
  // 2026-08-30: for a logged-in visitor this must be THEIR real, possibly-
  // 2026-09-01 — no discount is shown on this page any more, for anyone.
  // The 2TB welcome window still exists but is private to the file manager
  // (VaultPromoBanner); the plans page states one price per plan. Kept as a
  // named constant rather than deleting the branch below, so the "discounted
  // card" markup stays available if a future promo is ever meant to appear
  // here again — but nothing can turn it on by accident.
  // RIACCESO (2026-09-11): il ramo "prezzo pieno / oggi" della card 2 TB torna
  // a mostrare il 99 barrato sopra il 79. I numeri vengono dal server
  // (priceOf e prezzo2tbDopo, per valuta), mai scritti qui.
  const effective2tbPromo = (cur) => {
    const p = priceOf('lifetime_2tb', cur);
    if (!p?.amount || prezzo2tbDopo == null) return null;
    return { symbol: p.symbol, effective: p.amount, struck: prezzo2tbDopo };
  };
  // Formatted live from the real deadline (hooks/usePricing.js), never
  // hardcoded — this box used to say "After November 11, 2026" long after
  // the real deadline had become 2026-08-31, caught 2026-08-26.
  const deadlineDateText = deadline
    ? new Intl.DateTimeFormat(i18n.language === 'it' ? 'it-IT' : i18n.language === 'es' ? 'es-ES' : i18n.language === 'fr' ? 'fr-FR' : i18n.language === 'de' ? 'de-DE' : 'en-US', { day: 'numeric', month: 'long' }).format(deadline)
    : '';
  // One shared checkout implementation for every buy button on the site
  // (2026-08-28 unification) — this page used to have its own inline
  // startCheckout/handleBuy, duplicated from the hero/post-register flow's
  // useStartCheckout2TB, which meant a currency-detection or in-app-browser
  // fix made in one place had to be hand-copied to the other or it silently
  // didn't apply here. One hook instance per plan (each card needs its own
  // independent loading/error/in-app-warning state).
  const checkout2tb = useStartCheckout2TB('plans_2tb', 'lifetime_2tb');
  const checkout1tb = useStartCheckout2TB('plans_1tb', 'lifetime_1tb');
  const checkout5tb = useStartCheckout2TB('plans_5tb', 'lifetime_5tb');
  const checkoutFor = (planId) =>
    planId === 'lifetime_2tb' ? checkout2tb : planId === 'lifetime_5tb' ? checkout5tb : checkout1tb;

  const currentPlan = user?.plan?.id || user?.plan || 'free';
  const error = checkout2tb.error || checkout1tb.error;
  const inAppWarningProps = checkout2tb.inAppWarningProps || checkout1tb.inAppWarningProps;

  function handleBuy(planId) {
    if (planId === 'free') return;
    // Checkout requires a logged-in session (backend/routes/payments.js) — send
    // signed-out visitors to registration instead of a doomed 401 checkout call,
    // carrying the plan through so registration lands straight on Stripe
    // instead of the generic free/upgrade choice (2026-08-28). (In practice
    // this page only ever renders for authenticated users — see App.js's
    // routing — so this branch is currently unreachable; kept as a
    // defensive fallback.)
    if (!isAuthenticated) { onRegister?.(planId); return; }
    checkoutFor(planId).buy2tb();
  }

  return (
    <div className="plans-page">
      <div className="plans-header">
        <button className="btn-back" onClick={onBack}>{isAuthenticated ? t('nav_back') : t('nav_back_home')}</button>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:12}}>
          <div>
            <h2 style={{fontFamily:'Fraunces,serif',fontSize:36,fontWeight:700,marginBottom:8}}>{t('heading')}</h2>
            <p style={{color:'var(--text2)',fontSize:15}}>{t('subheading')}</p>
          </div>
          <CurrencySelector currency={currency} onChange={setCurrency} />
        </div>

        {isLaunch && (
          <div style={{marginTop:16,background:'rgba(34,197,94,.1)',border:'1px solid rgba(34,197,94,.3)',borderRadius:10,padding:'12px 18px',display:'flex',gap:10,alignItems:'center'}}>
            <span style={{fontSize:20}}>🎉</span>
            <div>
              <strong style={{color:'var(--green)'}}>{t('founder_offer_badge', {days: daysLeft})}</strong>
              <p style={{fontSize:13,color:'var(--text2)',marginTop:2}}>
                {t('founder_offer_text', { date: deadlineDateText })}
              </p>
            </div>
          </div>
        )}
        {/* The "referral discount ready" box that used to live here was removed
            2026-08-26 — it interrupted the purchase flow and implied a visitor
            about to pay full price was overpaying. Referral status still shows
            in the post-purchase confirmation email and on the Profile page. */}
      </div>

      {error && <div className="profile-msg error" style={{marginBottom:16}}>⚠ {error}</div>}

      <div className="plans-grid">
        {PLANS.map(plan => {
          const isCurrent = currentPlan === plan.id;
          const priceInfo = plan.priced ? priceOf(plan.id, currency) : null;
          // 2TB's struck/effective prices come from the same shared source as
          // every other "€99 → €79" banner on the site — never recomputed here.
          // 1TB has no coupon on Stripe (flat €49, see routes/payments.js) — no
          // struck price to show, so it's just the plain amount.
          const promo2tb = plan.id === 'lifetime_2tb' ? effective2tbPromo(currency) : null;
          const kp = KEY_PREFIX[plan.id];
          // Il limite per file della scheda viene dal server (usePricing ->
          // limiti), non da un numero scritto nella traduzione. Finche' non e'
          // arrivato la riga non si mostra: meglio una riga in meno che un
          // numero inventato.
          const limitePiano = limiteLeggibile(limiti?.[plan.id]?.maxFileSize, i18n.language);
          const features = Array.from({length: plan.featureCount}, (_, i) => t(`${plan.featuresFrom || kp}_feature_${i+1}`, { limite: limitePiano || '' }))
            .filter((f) => !/:\s*$|Max\s+per file/.test(f));
          return (
            <div key={plan.id} className={`plan-card ${plan.popular?'popular':''} ${isCurrent?'current':''}`}
              style={{'--plan-color':plan.color,'--pc':plan.color}}>
              {plan.popular && !isCurrent && <div className="popular-badge">{t('popular_badge')}</div>}
              {plan.founder && isLaunch && !isCurrent && <div className="popular-badge founder-badge">{t('card_founder_badge')}</div>}
              {isCurrent && <div className="current-badge">{t('current_badge')}</div>}
              {/* I posti, per ogni piano che ne ha (2026-09-07). Il piano
                  gratuito non ne ha e non mostra niente. */}
              {posti?.[plan.id] && (
                <div className={`cps-posti-grande${posti[plan.id].rimasti === 0 ? ' esauriti' : ''}`}>
                  <span className="cps-posti-numero">{posti[plan.id].rimasti}</span>
                  <span className="cps-posti-parola">
                    {posti[plan.id].rimasti === 0 ? t('posti_esauriti')
                      : posti[plan.id].rimasti === 1 ? t('posto_uno') : t('posti_disponibili')}
                  </span>
                </div>
              )}
              {plan.emoji && <div className="plan-emoji">{plan.emoji}</div>}
              <h3 className="plan-name" style={{color:plan.color}}>{t(`${kp}_name`)}</h3>
              <p className="plan-desc">{t(`${kp}_desc`)}</p>
              <div className="plan-storage-highlight" style={{background:plan.color+'15',border:`1px solid ${plan.color}33`}}>
                <span style={{color:plan.color,fontWeight:900,fontSize:24,fontFamily:'Fraunces,serif'}}>{plan.storage}</span>
                <span style={{fontSize:11,color:'var(--text3)',fontFamily:'JetBrains Mono,monospace',marginLeft:6}}>{t('storage_label')}</span>
              </div>
              <div className="plan-price">
                {!priceInfo ? (
                  <span className="price-free">{t('price_free')}</span>
                ) : promo2tb ? (
                  <div className="price-block">
                    <div className="price-row">
                      <span className="price-row-label">{t('full_price_label')}</span>
                      <span className="price-struck">{prezzoTesto(promo2tb.symbol, promo2tb.struck, i18n.language)}</span>
                    </div>
                    <div className="price-row">
                      <span className="price-row-label price-row-label-today">{t('today_label')}</span>
                      <span className="price-amount">{prezzoTesto(promo2tb.symbol, promo2tb.effective, i18n.language)}</span>
                    </div>
                    <span className="price-period"> {t('price_period')}</span>
                    <span className="price-vat">{t('price_vat')}</span>
                  </div>
                ) : (
                  <div className="price-block">
                    <span className="price-amount">{prezzoTesto(priceInfo.symbol, priceInfo.amount, i18n.language)}</span>
                    <span className="price-period"> {t('price_period')}</span>
                    <span className="price-vat">{t('price_vat')}</span>
                    {/* Stessa riga della scheda in home: solo finche' il server
                        dice che l'offerta 1TB e' attiva. Niente barrato, niente
                        "sconto" (49 e' il prezzo piu' basso degli ultimi 30
                        giorni); dopo il 13 sparisce da sola col prezzo a 59. */}
                    {plan.id === 'lifetime_1tb' && offerta1tbAttiva && scadenza1tb && (
                      <span className="price-vat">{t('plan_1tb_fino', { data: scadenza1tb })}</span>
                    )}
                  </div>
                )}
              </div>
              <ul className="plan-features">
                {features.map((f, i) => (
                  <li key={i}><span className="check" style={{color:plan.color}}>✓</span> {f}</li>
                ))}
              </ul>
              <button className="btn-plan" style={{'--plan-color':plan.color,'--pc':plan.color}}
                onClick={() => handleBuy(plan.id)}
                disabled={isCurrent || plan.id === 'free' || (plan.priced && checkoutFor(plan.id).loading)}>
                {plan.priced && checkoutFor(plan.id).loading ? t('btn_redirecting') :
                 isCurrent ? t('btn_current_plan') :
                 plan.id === 'free' ? t('btn_default') :
                 !isAuthenticated ? t('btn_sign_up_to_buy') :
                 t('btn_buy', {storage: plan.storage})}
              </button>
              {plan.priced && (
                <p className="cps-garanzia-riga cps-garanzia-card"><span aria-hidden="true">🛡️</span>{t('garanzia_riga')}</p>
              )}
            </div>
          );
        })}
      </div>
      {/* IL PERCHE' DEI POSTI, una volta sola, sotto tutte le card
          (2026-09-07). Stesso testo e stessa condizione della home: compare
          se almeno un piano e' a numero chiuso. */}
      {posti && Object.keys(posti).length > 0 && (
        <div className="cps-perche-posti">
          <h3>{t('perche_titolo')}</h3>
          <p>{t('perche_testo')}</p>
        </div>
      )}
      <p className="plans-footnote">{t('footnote')}</p>
      {inAppWarningProps && <InAppBrowserWarning {...inAppWarningProps} />}
    </div>
  );
}
